import clsx from "clsx";

import type { BoxProps } from "@/ui/atoms/Box";
import Box from "@/ui/atoms/Box";
import {
    Dropdown,
    DropdownContent,
    DropdownSelect,
    DropdownSelectItem,
    Icon,
    IconBox,
} from "@proda-ai/ui-kit";
import React from "react";
import type { TLabelsetInfo } from "@/apis/odin/apiODIN";

export type LabelsetInfoDropdownProps = BoxProps & {
    labelsetSourceId: number | null;
    labelsetExtractionInfo: Array<TLabelsetInfo>;
    onLabelsetSourceChange: (id: number | null) => void;
};

export const testIds = {
    main: "LabelsetInfoDropdown",
    dropdownBox: "LabelsetInfoDropdown-dropdownBox",
    optionML: "LabelsetInfoDropdown-optionML",
    section: "LabelsetInfoDropdown-section",
    optionLabelset: "LabelsetInfoDropdown-optionLabelset",
};
const LabelsetInfoDropdown = (props: LabelsetInfoDropdownProps) => {
    const {
        sx,
        className,
        labelsetExtractionInfo,
        labelsetSourceId,
        onLabelsetSourceChange,
        ...otherProps
    } = props;
    const [isLabelsetInfoDropdownVisible, setLabelsetInfoDropdownVisible] =
        React.useState(false);
    const optionML = { id: 0, item: "Machine Learning Predictions Only" };
    const labelsetOptions = labelsetExtractionInfo.map(labelset => {
        const additionalCount = labelset.matchingExtractionsCount;
        const additionalCountText =
            additionalCount > 0 ? ` (+${additionalCount} more)` : "";
        return {
            id: `${labelset.extractionId}`,
            item: `${labelset.fileName} ${additionalCountText} `,
        };
    });
    const detailedlabelsetOptions = Object.entries(
        labelsetExtractionInfo.reduce<Record<string, TLabelsetInfo[]>>(
            (acc, labelset) => {
                const dealName = labelset.dealName;
                if (!acc[dealName]) {
                    acc[dealName] = [];
                }
                acc[dealName] = [...acc[dealName], labelset];
                return acc;
            },
            {},
        ),
    ).map(([dealName, labelsets]) => ({ dealName, labelsets }));

    const selectedOption =
        labelsetOptions.find(option => Number(option.id) === labelsetSourceId)
            ?.item ?? optionML.item;

    return (
        <Box
            data-testid={testIds.main}
            className={clsx([testIds, className])}
            sx={{
                ...sx,
            }}
            {...otherProps}
        >
            <Dropdown
                isShowing={isLabelsetInfoDropdownVisible}
                onItemSelected={() => {}}
            >
                <Box
                    data-testid={testIds.dropdownBox}
                    sx={{
                        width: "100%",
                    }}
                    onClick={() => {
                        setLabelsetInfoDropdownVisible(
                            !isLabelsetInfoDropdownVisible,
                        );
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            border: "1px solid var(--proda-black-10)",
                            borderRadius: "4px",
                            marginTop: "8px",
                        }}
                    >
                        <Box
                            sx={{
                                padding: "8px",
                                fontSize: "12px",
                                height: "32px",
                                alignContent: "center",
                            }}
                        >
                            {selectedOption.length > 50
                                ? `${selectedOption.slice(0, 50)}...`
                                : selectedOption}
                        </Box>
                        <Box sx={{ alignContent: "center", height: "32px" }}>
                            <IconBox style={{ padding: "10px" }}>
                                <Icon icon="caret-down" size="sm" />
                            </IconBox>
                        </Box>
                    </Box>
                </Box>

                <DropdownContent>
                    <DropdownSelect
                        labelledById="options"
                        currentIndex={-1}
                        role="listbox"
                    >
                        <DropdownSelectItem
                            data-testid={testIds.optionML}
                            key="optionML"
                            label={
                                <Box
                                    sx={{
                                        textWrap: "wrap",
                                        color: "black",
                                    }}
                                >
                                    {optionML.item}
                                </Box>
                            }
                            isSelected={labelsetSourceId === null}
                            onClick={(): void => {
                                onLabelsetSourceChange(null);
                                setLabelsetInfoDropdownVisible(
                                    !isLabelsetInfoDropdownVisible,
                                );
                            }}
                        />

                        {detailedlabelsetOptions.map(item => (
                            <Box key={item.dealName}>
                                <Box
                                    data-testid={testIds.section}
                                    sx={{
                                        fontWeight: 700,
                                        color: "var(--proda-black-10)",
                                        fontSize: "12px",
                                        height: "32px",
                                        padding: "8px",
                                        backgroundColor:
                                            "var(--proda-white-l95)",
                                        borderTop:
                                            "1px solid var(--proda-white-150)",
                                    }}
                                >
                                    PROJECT: {item.dealName}
                                </Box>
                                {item.labelsets.map(labelset => {
                                    const similarityText = `${(labelset.similarity * 100).toFixed(0)}%`;
                                    const additionalCountText =
                                        labelset.matchingExtractionsCount > 0
                                            ? ` (+${labelset.matchingExtractionsCount} more)`
                                            : "";
                                    const fileText = `${labelset.fileName} ${additionalCountText}`;
                                    return (
                                        <Box
                                            data-testid={testIds.optionLabelset}
                                            data-labelset-source-id={
                                                labelset.extractionId
                                            }
                                            key={labelset.extractionId}
                                        >
                                            <DropdownSelectItem
                                                renderedOption={
                                                    <Box
                                                        sx={{
                                                            width: "100%",
                                                            display: "flex",
                                                            justifyContent:
                                                                "space-between",
                                                            fontSize: "12px",
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                textWrap:
                                                                    "wrap",
                                                            }}
                                                        >
                                                            {fileText}
                                                        </Box>
                                                        <Box
                                                            sx={{
                                                                paddingLeft:
                                                                    "3px",
                                                            }}
                                                        >
                                                            {similarityText}
                                                        </Box>
                                                    </Box>
                                                }
                                                label={fileText}
                                                isSelected={
                                                    labelset.extractionId ===
                                                    labelsetSourceId
                                                }
                                                onClick={(): void => {
                                                    onLabelsetSourceChange(
                                                        labelset.extractionId,
                                                    );
                                                    setLabelsetInfoDropdownVisible(
                                                        !isLabelsetInfoDropdownVisible,
                                                    );
                                                }}
                                            />
                                        </Box>
                                    );
                                })}
                            </Box>
                        ))}
                    </DropdownSelect>
                </DropdownContent>
            </Dropdown>
        </Box>
    );
};

export default LabelsetInfoDropdown;
